import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@restworld/utils-common';

export type OTPInputVariant = 'primary' | 'error';

export interface OTPInputProps {
  /** Number of OTP digits (default 6) */
  numberOfDigits?: number;
  /** Called with the OTP digits as a string on every change, so a leading zero is kept ("012345") */
  onChange: (otp: string) => void;
  /** Visual variant */
  variant?: OTPInputVariant;
  /** Pre-filled OTP value as a string */
  value?: string;
  /** Whether OTP was successfully verified */
  isSuccess: boolean;
  /** Additional className for individual digit fields */
  fieldClassName?: string;
}

const generateEmptyArray = (length: number) => Array.from({ length }, () => '');

// Digits only. `isNaN(Number(...))` let a space through (`Number(' ') === 0`), which the old
// `parseInt` hid by truncating; now that the digits are passed on as typed, it has to be refused.
const DIGITS_ONLY = /^\d*$/;

export function OTPInput({
  numberOfDigits = 6,
  onChange,
  variant = 'primary',
  value,
  isSuccess = false,
  fieldClassName,
}: OTPInputProps) {
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const initialOtpDigits = useMemo(() => generateEmptyArray(numberOfDigits), [numberOfDigits]);
  const [otpDigits, setOtpDigits] = useState<string[]>(initialOtpDigits);

  useEffect(() => {
    const updated = generateEmptyArray(numberOfDigits);
    if (value && DIGITS_ONLY.test(value)) {
      value.split('').forEach((digit, index) => {
        updated[index] = digit;
      });
    }
    setOtpDigits(updated);
  }, [numberOfDigits, value]);

  const handleInputChange = useCallback(
    (index: number, val: string) => {
      if (!DIGITS_ONLY.test(val)) return;

      // iOS `one-time-code` autofill can drop the whole code into a single cell
      // (maxLength limits typing, not an autofilled value). Spread it across the
      // cells like a paste instead of cramming every digit into cell `index`.
      if (val.length > 1) {
        const digits = val.split('').slice(0, numberOfDigits);
        const spread = digits.concat(generateEmptyArray(numberOfDigits - digits.length));
        setOtpDigits(spread);
        inputRefs.current[digits.length - 1]?.focus();
        onChange(spread.join(''));
        return;
      }

      const updated = [...otpDigits];
      updated[index] = val;
      setOtpDigits(updated);
      if (val && index < numberOfDigits - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      // A string, not a number: `parseInt('012345')` is 12345, and the parent hands that back as
      // `value`, so the boxes refilled from "12345" and the leading zero could never be typed.
      onChange(updated.join(''));
    },
    [otpDigits, numberOfDigits, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      // Codes copied from an SMS or WhatsApp often carry a trailing space or newline
      const pasteData = e.clipboardData.getData('text/plain').trim();
      if (pasteData && DIGITS_ONLY.test(pasteData)) {
        const updated = pasteData.split('').slice(0, numberOfDigits);
        setOtpDigits(updated);
        onChange(updated.join(''));
      }
    },
    [numberOfDigits, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        const updated = [...otpDigits];
        updated[index] = '';
        setOtpDigits(updated);
        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      }
    },
    [otpDigits]
  );

  const isErrored = variant === 'error';

  return (
    <div className="flex w-full flex-row justify-center gap-1">
      {otpDigits.map((digit, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric"
          maxLength={1}
          // one-time-code on the first cell lets iOS Safari offer the SMS code above the keyboard
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => handleKeyDown(e, index)}
          ref={(el) => {
            if (el) inputRefs.current[index] = el;
          }}
          className={cn(
            // Same field look as EmailInput: neutral background, 1px neutral border, 8px radius.
            'box-border h-12 min-w-0 flex-1 max-w-12 rounded-lg border border-neutral-90 bg-neutral-10 text-center text-base font-medium leading-5 text-neutral-700',
            'outline-none hover:bg-neutral-30 focus:border-primary-300 focus:ring-1 focus:ring-primary-300',
            isErrored && '!bg-red-50 border-red-500 text-red-700 focus:border-red-500 focus:ring-red-500',
            isSuccess && 'border-green-500 focus:border-green-500 focus:ring-green-500',
            fieldClassName
          )}
        />
      ))}
    </div>
  );
}
